import { EQuestionType, TQuestion, TQuestionOption } from '@/types/QuestionBank';
import { DeleteOutlined, MenuOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Checkbox, Flex, Form, Input, message, Modal, Radio, Typography } from 'antd';
import arrayMove from 'array-move';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import cx from 'classnames';
import { useRequest } from '@@/plugin-request/request';
import { createQuestion, updateQuestion } from '@/services/QuestionBank';
import CampSettingsEditor from '@/pages/backend/components/CampSettingsEditor';
import styles from './index.less';
import { isEmpty } from 'lodash';

const ChoiceItem = ({
  type,
  option,
  setOptions,
  showDelBtn,
}: {
  type: EQuestionType;
  option: TQuestionOption;
  setOptions: any;
  showDelBtn: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: option.index,
  });
  const [empty, setEmpty] = useState<boolean>(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    setEmpty(!option.description);
  }, [option]);

  const item = (
    <Flex gap={'small'} justify={'space-between'} align={'center'}>
      <div style={{ width: '514px' }}>
        <Input
          placeholder={'请输入选项内容…'}
          value={option.description}
          showCount
          maxLength={100}
          className={cx(empty ? 'ant-input-status-error' : '')}
          onChange={(e) => {
            setOptions((prev: TQuestionOption[]) => {
              return prev.map((item) => {
                if (item.index === option.index) {
                  return {
                    ...item,
                    description: e.target.value,
                  };
                }
                return item;
              });
            });
          }}
        />
      </div>
      <div {...listeners}>
        <MenuOutlined className={cx('text-808696')} />
      </div>
      {showDelBtn && (
        <div>
          <DeleteOutlined
            className={cx('text-808696')}
            onClick={() => {
              setOptions((prev: TQuestionOption[]) => {
                return prev.filter((item) => item.index !== option.index);
              });
            }}
          />
        </div>
      )}
    </Flex>
  );

  return (
    <div key={option.index} ref={setNodeRef} style={style} {...attributes}>
      <Form.Item>
        {type === EQuestionType.SINGLE_CHOICE ? (
          <Radio key={option.index} value={option.index}>
            {item}
          </Radio>
        ) : (
          <Checkbox key={option.index} value={option.index}>
            {item}
          </Checkbox>
        )}
        {empty && (
          <div className={cx('ant-form-item-explain-error', 'ml-[24px]')}>选项内容不能为空</div>
        )}
      </Form.Item>
    </div>
  );
};

const defaultSingleOptions = [
  {
    index: 1,
    description: '选项一',
    answer: true,
  },
  {
    index: 2,
    description: '选项二',
    answer: false,
  },
  {
    index: 3,
    description: '选项三',
    answer: false,
  },
];

const defaultMutipleOptions = [
  {
    index: 1,
    description: '选项一',
    answer: true,
  },
  {
    index: 2,
    description: '选项二',
    answer: true,
  },
  {
    index: 3,
    description: '选项三',
    answer: false,
  },
];

/**
 * 题目表单
 * @constructor
 */
const QuestionFormModal = ({
  isEdit,
  propsData,
  isModalOpen,
  setIsModalOpen,
  refresh,
  setRefresh,
}: {
  isEdit: boolean;
  propsData?: TQuestion;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  refresh: number;
  setRefresh: (refresh: number) => void;
}) => {
  const [selectedType, setSelectedType] = useState<EQuestionType>(EQuestionType.SINGLE_CHOICE);
  const [singleOptions, setSingleOptions] = useState<TQuestionOption[]>([]); // 单选选项
  const [multipleOptions, setMultipleOptions] = useState<TQuestionOption[]>([]); // 多选选项

  const [answer, setAnswer] = useState<boolean>(true);
  const [form] = Form.useForm();

  const description = useMemo(() => {
    if (propsData?.description && typeof propsData?.description === 'string') {
      try {
        return JSON.parse(propsData?.description);
      } catch (e) {
        return '';
      }
    }
    return '';
  }, [propsData?.description]);

  const analysis = useMemo(() => {
    if (propsData?.analysis && typeof propsData?.analysis === 'string') {
      try {
        return JSON.parse(propsData?.analysis);
      } catch (e) {
        return '';
      }
    }
    return '';
  }, [propsData?.analysis]);

  useEffect(() => {
    if (propsData) {
      setSelectedType(propsData.type || EQuestionType.SINGLE_CHOICE);
      switch (propsData.type) {
        case EQuestionType.SINGLE_CHOICE:
          {
            setSingleOptions(propsData.info?.options || defaultSingleOptions);
            setMultipleOptions(defaultMutipleOptions);
          }
          break;
        case EQuestionType.MULTIPLE_CHOICE:
          {
            setMultipleOptions(propsData.info?.options || defaultMutipleOptions);
            setSingleOptions(defaultSingleOptions);
          }
          break;
        default: {
          setMultipleOptions(defaultMutipleOptions);
          setSingleOptions(defaultSingleOptions);
        }
      }
      if (propsData.info) {
        setAnswer(propsData.info.answer);
      }
      form.setFieldsValue({ ...propsData });
    }
  }, [propsData, isModalOpen]);

  const handleSingleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSingleOptions((prev: TQuestionOption[]) => {
        const oldIndex = prev.findIndex((item) => item.index === active.id);
        const newIndex = prev.findIndex((item) => item.index === over?.id);

        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, []);

  const handleMutipleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setMultipleOptions((prev: TQuestionOption[]) => {
        const oldIndex = prev.findIndex((item) => item.index === active.id);
        const newIndex = prev.findIndex((item) => item.index === over?.id);

        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, []);

  const { run: submitQuestionRun } = useRequest(
    (form: TQuestion) => {
      if (form.id) {
        return updateQuestion(form.id!, form);
      } else {
        return createQuestion(form);
      }
    },
    {
      manual: true,
      onSuccess: () => {
        message.success('保存成功');
      },
      onError: (err) => {
        message.error(err.message);
      },
    },
  );

  const checkFormComplete = () => {
    switch (selectedType) {
      case EQuestionType.SINGLE_CHOICE:
      case EQuestionType.MULTIPLE_CHOICE:
        {
          const items = (
            selectedType === EQuestionType.SINGLE_CHOICE ? singleOptions : multipleOptions
          ).map((item) => item?.description.trim());
          if (items.length < 2) {
            return false;
          }
          const hasEmptyOrRepeated = items.some(
            (item, index) => isEmpty(item) || items.indexOf(item, index + 1) > 0,
          );
          if (hasEmptyOrRepeated) {
            return false;
          }
          if (selectedType === EQuestionType.MULTIPLE_CHOICE) {
            // 至少选中2个选项
            const hasSelected2 = multipleOptions.filter((item) => item.answer).length >= 2;
            if (!hasSelected2) {
              message.error('请至少选中2个选项');
              return false;
            }
          }
        }
        break;
    }
    return true;
  };

  return (
    <Modal
      className={styles.ModalWrapper}
      open={isModalOpen}
      title={isEdit ? '编辑题目' : '新建题目'}
      destroyOnClose
      width={868}
      onCancel={() => {
        setIsModalOpen(false);
      }}
      onOk={() => {
        if (!checkFormComplete()) {
          return;
        }
        form.validateFields().then((values) => {
          let options =
            selectedType === EQuestionType.SINGLE_CHOICE ? singleOptions : multipleOptions;
          submitQuestionRun({
            ...propsData,
            ...values,
            description:
              typeof values.description === 'string'
                ? values.description
                : JSON.stringify(values.description),
            analysis:
              typeof values.analysis === 'string'
                ? values.analysis
                : JSON.stringify(values.analysis),
            type: selectedType,
            info: {
              options: options.map((item, index) => ({ ...item, index })),
              answer,
            },
          }).then(() => {
            setIsModalOpen(false);
          });
        });
      }}
      afterClose={() => {
        form.resetFields();
        setSelectedType(EQuestionType.SINGLE_CHOICE);
        setSingleOptions(defaultSingleOptions);
        setMultipleOptions(defaultMutipleOptions);
        setAnswer(true);
        setRefresh(refresh + 1);
      }}
    >
      <Form
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 18 }}
        form={form}
        className={cx('m-[24px]')}
        clearOnDestroy
      >
        <Form.Item
          label={'题目标题'}
          name={'title'}
          required
          rules={[{ required: true, message: '题目标题不能为空' }]}
        >
          <Input showCount maxLength={100} placeholder={'请输入…'} />
        </Form.Item>
        <Form.Item name={'description'} label={'题目描述'}>
          <CampSettingsEditor
            content={description}
            placeholder={'请输入…'}
            style={{ height: '240px' }}
          />
        </Form.Item>
        <Form.Item
          label={'题目类型'}
          name={'type'}
          initialValue={selectedType}
          required
          rules={[{ required: true, message: '题目类型不能为空' }]}
        >
          <Radio.Group
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value);
            }}
          >
            <Radio value={EQuestionType.SINGLE_CHOICE}>单选题</Radio>
            <Radio value={EQuestionType.MULTIPLE_CHOICE}>多选题</Radio>
            <Radio value={EQuestionType.TURE_OR_FALSE}>判断题</Radio>
            <Radio value={EQuestionType.SUBJECTIVE}>主观题</Radio>
          </Radio.Group>
        </Form.Item>
        {(selectedType === EQuestionType.MULTIPLE_CHOICE ||
          selectedType === EQuestionType.SINGLE_CHOICE) && (
          <Form.Item label={'选项'} required>
            <Typography.Text className={cx('text-c0c0c0', 'leading-8')}>
              请填写选项，并选中本题正确答案
            </Typography.Text>
            {selectedType === EQuestionType.SINGLE_CHOICE && (
              <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleSingleDragEnd}>
                <SortableContext
                  items={singleOptions.map((item) => ({ ...item, id: item.index })) || []}
                  strategy={verticalListSortingStrategy}
                >
                  <Radio.Group
                    value={singleOptions.find((item) => item.answer)?.index}
                    onChange={(e) => {
                      setSingleOptions((prev: TQuestionOption[]) => {
                        return prev.map((item) => {
                          if (item.index === e.target.value) {
                            return {
                              ...item,
                              answer: true,
                            };
                          }
                          return {
                            ...item,
                            answer: false,
                          };
                        });
                      });
                    }}
                  >
                    {singleOptions.map((option) => {
                      return (
                        <ChoiceItem
                          type={selectedType}
                          option={option}
                          setOptions={setSingleOptions}
                          showDelBtn={singleOptions.length > 2}
                        />
                      );
                    })}
                  </Radio.Group>
                </SortableContext>
              </DndContext>
            )}
            {selectedType === EQuestionType.MULTIPLE_CHOICE && (
              <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleMutipleDragEnd}>
                <SortableContext
                  items={multipleOptions.map((item) => ({ ...item, id: item.index })) || []}
                  strategy={verticalListSortingStrategy}
                >
                  <Checkbox.Group
                    value={multipleOptions.filter((item) => item.answer).map((item) => item.index)}
                    onChange={(e) => {
                      setMultipleOptions((prev: TQuestionOption[]) => {
                        return prev.map((item) => {
                          if (e.includes(item.index)) {
                            return {
                              ...item,
                              answer: true,
                            };
                          }
                          return {
                            ...item,
                            answer: false,
                          };
                        });
                      });
                    }}
                  >
                    {multipleOptions.map((option) => {
                      return (
                        <ChoiceItem
                          type={selectedType}
                          option={option}
                          setOptions={setMultipleOptions}
                          showDelBtn={multipleOptions.length > 2}
                        />
                      );
                    })}
                  </Checkbox.Group>
                </SortableContext>
              </DndContext>
            )}
            <Button
              type={'link'}
              icon={<PlusSquareOutlined />}
              onClick={() => {
                switch (selectedType) {
                  case EQuestionType.SINGLE_CHOICE:
                    {
                      if (singleOptions.length < 50) {
                        setSingleOptions([
                          ...singleOptions,
                          {
                            index: Math.max(...singleOptions.map((item) => item.index)) + 1,
                            description: '',
                            answer: false,
                          },
                        ]);
                      } else {
                        message.error('选项最多50个');
                      }
                    }
                    break;
                  case EQuestionType.MULTIPLE_CHOICE:
                    {
                      if (multipleOptions.length < 50) {
                        setMultipleOptions([
                          ...multipleOptions,
                          {
                            index: Math.max(...multipleOptions.map((item) => item.index)) + 1,
                            description: '',
                            answer: false,
                          },
                        ]);
                      } else {
                        message.error('选项最多50个');
                      }
                    }
                    break;
                }
              }}
            >
              新建选项
            </Button>
          </Form.Item>
        )}
        {selectedType === EQuestionType.TURE_OR_FALSE && (
          <Form.Item
            label={'正确答案'}
            required
            rules={[{ required: true, message: '正确答案不能为空' }]}
          >
            <Radio.Group
              value={answer}
              onChange={(e) => {
                setAnswer(e.target.value);
              }}
            >
              <Radio value={true}>正确</Radio>
              <Radio value={false}>错误</Radio>
            </Radio.Group>
          </Form.Item>
        )}
        <Form.Item name={'analysis'} label={'题目解析'}>
          <CampSettingsEditor
            content={analysis}
            placeholder={'请输入…'}
            style={{ height: '240px' }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default QuestionFormModal;
